import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { estimateAPI, shipmentAPI } from '../../services/api';

/**
 * Formats a number as USD currency: $X,XXX.XX
 */
const formatCurrency = (value) => {
  if (value == null || isNaN(value)) return '-';
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Formats a percentage value for display.
 */
const formatPercent = (value) => {
  if (value == null || isNaN(value)) return '-';
  return `${Number(value).toFixed(2)}%`;
};

/**
 * Maps source type to a human-readable label.
 */
const sourceLabel = (source) => {
  if (!source) return 'Unknown';
  switch (source) {
    case 'rate_card': return 'Rate Card';
    case 'spot_market': return 'Spot Market (FBX)';
    case 'ai_estimate': return 'AI Estimate';
    case 'manual': return 'Manual Override';
    case 'own_model': return 'Own Model';
    default: return source.replace(/_/g, ' ');
  }
};

/**
 * Confidence badge color based on level.
 */
const confidenceColor = (confidence) => {
  if (confidence == null) return '#8a8784';
  if (confidence >= 0.8) return '#16a34a';
  if (confidence >= 0.5) return '#ea580c';
  return '#dc2626';
};

/**
 * EstimateCalculator — V2 full landed cost calculator with US customs breakdown.
 *
 * Displays:
 *   - FOB Value
 *   - Ocean Freight (with forwarder/AI details)
 *   - Full US Customs Breakdown (duty, MPF, HMF, Section 301, broker fees)
 *   - Drayage (customer-specific rate card)
 *   - Insurance & Disbursements
 *   - Marteu Fee
 *   - TOTAL LANDED COST
 *
 * Each field supports admin override. Warnings shown for missing rate cards.
 */
const EstimateCalculator = () => {
  const { shipmentId } = useParams();
  const [shipment, setShipment] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);

  // Admin override state — mirrors the estimate fields for editing
  const [overrides, setOverrides] = useState({});
  const [editingField, setEditingField] = useState(null);

  const fetchShipment = useCallback(async () => {
    if (!shipmentId) return;
    try {
      const result = await shipmentAPI.getById(shipmentId);
      setShipment(result.data || null);
    } catch (err) {
      console.error('EstimateCalculator: failed to load shipment', err);
    }
  }, [shipmentId]);

  const fetchEstimate = useCallback(async () => {
    if (!shipmentId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await estimateAPI.getEstimate(shipmentId);
      setEstimate(result.data || null);
    } catch (err) {
      if (err.response?.status === 404) {
        setEstimate(null);
      } else {
        setError('Failed to load estimate');
        console.error('EstimateCalculator fetch error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    fetchShipment();
    fetchEstimate();
  }, [fetchShipment, fetchEstimate]);

  /**
   * Trigger V2 calculation on the backend.
   */
  const handleCalculate = async () => {
    try {
      setCalculating(true);
      setError(null);
      setFeedback(null);
      await estimateAPI.calculate(shipmentId);
      await fetchEstimate();
      setFeedback({ type: 'success', message: 'Estimate calculated successfully' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to calculate estimate';
      setError(msg);
      console.error('EstimateCalculator calculate error:', err);
    } finally {
      setCalculating(false);
    }
  };

  /**
   * Finalize the estimate with any admin overrides.
   */
  const handleFinalize = async () => {
    try {
      setFinalizing(true);
      setError(null);
      setFeedback(null);
      const data = {
        overrides: Object.keys(overrides).length > 0 ? overrides : undefined
      };
      await estimateAPI.finalize(shipmentId, data);
      await fetchEstimate();
      setOverrides({});
      setEditingField(null);
      setFeedback({ type: 'success', message: 'Estimate finalized successfully' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to finalize estimate';
      setError(msg);
      console.error('EstimateCalculator finalize error:', err);
    } finally {
      setFinalizing(false);
    }
  };

  /**
   * Start editing a specific line-item field.
   */
  const startEdit = (field, currentValue) => {
    setEditingField(field);
    setOverrides((prev) => ({
      ...prev,
      [field]: currentValue ?? ''
    }));
  };

  /**
   * Update override value for a field.
   */
  const updateOverride = (field, value) => {
    setOverrides((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Cancel an in-progress edit and remove the override.
   */
  const cancelEdit = (field) => {
    setEditingField(null);
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Estimate Calculator</h1>
        </div>
        <div className="loading">Loading estimate data...</div>
      </div>
    );
  }

  const bd = estimate?.breakdown || {};
  const customs = bd.customs || {};
  const warnings = estimate?.warnings || [];
  const confidencePercent = estimate?.confidence != null
    ? Math.round(estimate.confidence * 100)
    : null;

  /**
   * Render an editable line item row.
   * Shows the computed value, the source badge, and an Edit button.
   * When editing, replaces the value cell with a number input.
   */
  const renderLine = (label, field, value, source, extra) => {
    const isEditing = editingField === field;
    const displayValue = overrides[field] != null && !isEditing
      ? Number(overrides[field])
      : value;

    return (
      <div className="estimate-line" key={field}>
        <span className="estimate-line-label">{label}</span>
        <span className="estimate-line-value">
          {isEditing ? (
            <input
              className="admin-inline-input admin-inline-narrow"
              type="number"
              step="0.01"
              value={overrides[field] ?? ''}
              onChange={(e) => updateOverride(field, e.target.value)}
              autoFocus
            />
          ) : (
            formatCurrency(displayValue)
          )}
        </span>
        <span className="estimate-line-source">
          {source && (
            <span className="badge badge-tag">{sourceLabel(source)}</span>
          )}
          {overrides[field] != null && !isEditing && (
            <span className="badge badge-tag" style={{ background: '#ea580c', color: 'white', marginLeft: '0.25rem' }}>
              overridden
            </span>
          )}
        </span>
        <span className="estimate-line-actions">
          {isEditing ? (
            <>
              <button className="btn btn-sm btn-primary" onClick={() => setEditingField(null)}>
                Set
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => cancelEdit(field)}>
                Cancel
              </button>
            </>
          ) : (
            <button className="btn btn-sm btn-secondary" onClick={() => startEdit(field, value)}>
              Edit
            </button>
          )}
        </span>
        {extra && <div className="estimate-line-extra text-small">{extra}</div>}
      </div>
    );
  };

  return (
    <div className="admin-page estimate-calculator">
      <div className="page-header">
        <h1>
          V2 Estimate Calculator
          {shipment && (
            <span className="text-small" style={{ marginLeft: '1rem', fontWeight: 400 }}>
              Shipment {shipment.orderId || shipmentId.slice(0, 8)}
            </span>
          )}
        </h1>
        <div className="header-actions">
          <Link to={`/shipments/${shipmentId}`} className="btn btn-secondary">
            Back to Shipment
          </Link>
          <button
            className="btn btn-primary"
            onClick={handleCalculate}
            disabled={calculating}
          >
            {calculating ? 'Calculating...' : 'Calculate'}
          </button>
          {estimate && (
            <button
              className="btn btn-primary"
              onClick={handleFinalize}
              disabled={finalizing}
              style={{ background: '#16a34a' }}
            >
              {finalizing ? 'Finalizing...' : 'Finalize Estimate'}
            </button>
          )}
        </div>
      </div>

      {feedback && (
        <div className={feedback.type === 'success' ? 'success-banner' : 'error-message'}
          style={{ marginBottom: '1rem' }}>
          <p>{feedback.message}</p>
        </div>
      )}

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          <p>{error}</p>
        </div>
      )}

      {/* Warnings banner */}
      {warnings.length > 0 && (
        <div className="warning-banner" style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 'var(--radius-md)',
          padding: '0.75rem 1rem',
          marginBottom: '1rem'
        }}>
          <strong style={{ color: '#ea580c' }}>Warnings:</strong>
          <ul style={{ margin: '0.25rem 0 0 1.25rem', color: '#9a3412' }}>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {!estimate ? (
        <div className="empty-state" style={{ padding: '2rem' }}>
          <p>No V2 estimate calculated yet for this shipment.</p>
          <p className="text-small" style={{ marginTop: '0.5rem' }}>
            Click "Calculate" to generate a full landed cost breakdown.
          </p>
        </div>
      ) : (
        <div className="detail-grid">
          {/* Shipment Summary */}
          {shipment && (
            <div className="detail-section">
              <h2>Shipment Summary</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Origin</label>
                  <span>{shipment.originPort}</span>
                </div>
                <div className="info-item">
                  <label>Destination</label>
                  <span>{shipment.destinationPort}</span>
                </div>
                <div className="info-item">
                  <label>Container</label>
                  <span>{shipment.containerCount}x {shipment.containerType}</span>
                </div>
                <div className="info-item">
                  <label>Commodity</label>
                  <span>{shipment.commodity || 'General cargo'}</span>
                </div>
                {shipment.totalWeight && (
                  <div className="info-item">
                    <label>Weight</label>
                    <span>{shipment.totalWeight} kg</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FOB Value */}
          <div className="detail-section full-width">
            <h2>FOB Value</h2>
            <div className="estimate-lines">
              {renderLine('FOB Value (Order Total)', 'fobValue', bd.fobValue, null)}
            </div>
          </div>

          {/* Ocean Freight */}
          <div className="detail-section full-width">
            <h2>Ocean Freight</h2>
            <div className="estimate-lines">
              {renderLine(
                'Ocean Freight (Base Rate)',
                'oceanFreight',
                bd.oceanFreight,
                bd.oceanSource,
                bd.oceanForwarder ? `Forwarder: ${bd.oceanForwarder}` : null
              )}
              {bd.fuelSurcharge != null && renderLine(
                'Fuel Surcharge',
                'fuelSurcharge',
                bd.fuelSurcharge,
                bd.oceanSource
              )}
            </div>
            {/* AI Reasoning */}
            {bd.oceanAiReasoning && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: 'var(--bg-muted)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.85rem',
                color: 'var(--text-secondary)'
              }}>
                <strong>AI Reasoning:</strong> {bd.oceanAiReasoning}
              </div>
            )}
          </div>

          {/* US Customs Breakdown */}
          <div className="detail-section full-width">
            <h2>US Customs Breakdown</h2>
            <div className="estimate-lines">
              {renderLine(
                `Duty (${formatPercent(customs.dutyRate)})`,
                'dutyAmount',
                customs.dutyAmount,
                'customs'
              )}
              {renderLine(
                `MPF (${formatPercent(customs.mpfRate)}, min ${formatCurrency(customs.mpfMin)} / max ${formatCurrency(customs.mpfMax)})`,
                'mpfAmount',
                customs.mpfAmount,
                'customs'
              )}
              {renderLine(
                `HMF (${formatPercent(customs.hmfRate)})`,
                'hmfAmount',
                customs.hmfAmount,
                'customs'
              )}
              {customs.section301Rate != null && customs.section301Rate > 0 && renderLine(
                `Section 301 Tariff (${formatPercent(customs.section301Rate)})`,
                'section301Amount',
                customs.section301Amount,
                'customs'
              )}
              {customs.additionalTariffRate != null && customs.additionalTariffRate > 0 && renderLine(
                `Additional Tariffs (${formatPercent(customs.additionalTariffRate)})`,
                'additionalTariffAmount',
                customs.additionalTariffAmount,
                'customs'
              )}

              {/* Broker Fees sub-section */}
              <div className="estimate-divider" />
              <div className="estimate-line" style={{ fontStyle: 'italic' }}>
                <span className="estimate-line-label"><strong>Broker Fees</strong></span>
                <span className="estimate-line-value"></span>
                <span className="estimate-line-source">
                  {customs.brokerForwarder && (
                    <span className="badge badge-tag">{customs.brokerForwarder}</span>
                  )}
                </span>
                <span className="estimate-line-actions"></span>
              </div>
              {renderLine(
                'Customs Clearance',
                'brokerClearanceFee',
                customs.brokerClearanceFee,
                null
              )}
              {renderLine(
                'ISF Filing',
                'brokerIsfFee',
                customs.brokerIsfFee,
                null
              )}
              {renderLine(
                'CFC / Continuous Bond',
                'brokerCfcFee',
                customs.brokerCfcFee,
                null
              )}

              <div className="estimate-divider" />
              <div className="estimate-line estimate-total-line">
                <span className="estimate-line-label"><strong>Total US Customs</strong></span>
                <span className="estimate-line-value estimate-total-value">
                  {formatCurrency(customs.totalCustoms)}
                </span>
                <span className="estimate-line-source"></span>
                <span className="estimate-line-actions"></span>
              </div>
            </div>
          </div>

          {/* Drayage */}
          <div className="detail-section full-width">
            <h2>Drayage (Destination Trucking)</h2>
            <div className="estimate-lines">
              {renderLine(
                'Drayage',
                'drayage',
                bd.drayage,
                bd.drayageSource,
                [
                  bd.drayageCustomer && `Customer: ${bd.drayageCustomer}`,
                  bd.drayageDistanceMiles && `Distance: ${bd.drayageDistanceMiles} mi`,
                  bd.drayageProvider && `Provider: ${bd.drayageProvider}`
                ].filter(Boolean).join(' | ') || null
              )}
            </div>
          </div>

          {/* Insurance & Disbursements */}
          <div className="detail-section full-width">
            <h2>Other Charges</h2>
            <div className="estimate-lines">
              {renderLine(
                'Insurance',
                'insurance',
                bd.insurance,
                null
              )}
              {renderLine(
                'Disbursements',
                'disbursements',
                bd.disbursements,
                null
              )}
              {renderLine(
                'Marteu Fee',
                'marteuFee',
                bd.marteuFee,
                null
              )}
            </div>
          </div>

          {/* TOTAL LANDED COST */}
          <div className="detail-section full-width" style={{
            background: 'var(--bg-warm)',
            border: '2px solid var(--accent-color)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <div className="estimate-lines">
              <div className="estimate-line estimate-total-line" style={{ padding: '0.5rem 0' }}>
                <span className="estimate-line-label" style={{ fontSize: '1.125rem' }}>
                  <strong>TOTAL LANDED COST</strong>
                </span>
                <span className="estimate-line-value estimate-total-value" style={{ fontSize: '1.25rem' }}>
                  {formatCurrency(estimate.totalEstimated || estimate.total)}
                </span>
                <span className="estimate-line-source"></span>
                <span className="estimate-line-actions"></span>
              </div>
              {estimate.totalLow != null && estimate.totalHigh != null && (
                <div className="estimate-line">
                  <span className="estimate-line-label text-small">Range</span>
                  <span className="estimate-line-value text-small">
                    {formatCurrency(estimate.totalLow)} &ndash; {formatCurrency(estimate.totalHigh)}
                  </span>
                  <span className="estimate-line-source"></span>
                  <span className="estimate-line-actions"></span>
                </div>
              )}
            </div>
          </div>

          {/* Confidence */}
          {confidencePercent != null && (
            <div className="detail-section full-width">
              <h2>Confidence</h2>
              <div className="estimate-confidence">
                <span className="estimate-confidence-label">Level:</span>
                <div className="estimate-confidence-bar">
                  <div
                    className="estimate-confidence-fill"
                    style={{
                      width: `${confidencePercent}%`,
                      background: confidenceColor(estimate.confidence)
                    }}
                  />
                </div>
                <span className="estimate-confidence-value" style={{
                  color: confidenceColor(estimate.confidence)
                }}>
                  {confidencePercent}%
                </span>
              </div>
            </div>
          )}

          {/* Admin Overrides Summary */}
          {Object.keys(overrides).length > 0 && (
            <div className="detail-section full-width" style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 'var(--radius-md)'
            }}>
              <h2>Pending Overrides</h2>
              <table className="simple-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Original</th>
                    <th>Override</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(overrides).map(([field, value]) => (
                    <tr key={field}>
                      <td>{field}</td>
                      <td>{formatCurrency(getOriginalValue(bd, customs, field))}</td>
                      <td><strong>{formatCurrency(Number(value))}</strong></td>
                      <td>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => cancelEdit(field)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-small" style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
                Click "Finalize Estimate" to apply these overrides.
              </p>
            </div>
          )}

          {/* Estimate Metadata */}
          <div className="detail-section full-width">
            <h2>Metadata</h2>
            <div className="info-grid">
              <div className="info-item">
                <label>Version</label>
                <span>{estimate.version || '-'}</span>
              </div>
              <div className="info-item">
                <label>Status</label>
                <span className={`badge ${estimate.status === 'FINALIZED' ? 'badge-active' : 'badge-tag'}`}>
                  {estimate.status || 'DRAFT'}
                </span>
              </div>
              <div className="info-item">
                <label>Source</label>
                <span>{sourceLabel(estimate.source)}</span>
              </div>
              {estimate.createdAt && (
                <div className="info-item">
                  <label>Created</label>
                  <span>{new Date(estimate.createdAt).toLocaleString()}</span>
                </div>
              )}
              {estimate.finalizedAt && (
                <div className="info-item">
                  <label>Finalized</label>
                  <span>{new Date(estimate.finalizedAt).toLocaleString()}</span>
                </div>
              )}
              {estimate.finalizedBy && (
                <div className="info-item">
                  <label>Finalized By</label>
                  <span>{estimate.finalizedBy}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper: resolve original (pre-override) value for a field from the breakdown/customs objects.
 */
function getOriginalValue(breakdown, customs, field) {
  // Check customs fields first
  if (customs[field] != null) return customs[field];
  // Check top-level breakdown fields
  if (breakdown[field] != null) return breakdown[field];
  return null;
}

export default EstimateCalculator;
